'use client'

import { Box, chakra, Flex, Text, VStack } from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { glassPanel } from '@/shared/lib/chakra/glass'

interface AuthLayoutProps {
  title: string
  description: string
  children: ReactNode
  maxW?: string
}

const QUOTE = {
  ru: {
    support: 'написать в поддержку',
    helpText:
      'Если вы не можете войти, обратитесь к администратору учреждения или',
  },
  uz: {
    support: "qo'llab-quvvatlashga yozing",
    helpText: "Agar kira olmasangiz, muassasa ma'muriga murojaat qiling yoki",
  },
}

// 24px-wide encode of background.webp, inlined so it paints with the HTML and
// gets blurred up by CSS while the full image downloads.
const BG_PLACEHOLDER =
  'data:image/webp;base64,UklGRowAAABXRUJQVlA4IIAAAABQBACdASoYAA4APu1iqU2ppaOiMAgBMB2JYgC7ACHw7vd4FrEk/sHkemn4AP7BLB56Sjz/RBGduGNqm+q7pSif7QfED1LqUZrnEzKaDLIEU3STasvXkzX1h8wxbP8vn+S9cOCn0TY67IOQnfC1b8BNXFx8D/T363K9k2nGdEAAAA=='

function AuthBackground() {
  const imgRef = useRef<HTMLImageElement>(null)
  const [loaded, setLoaded] = useState(false)

  // A cached image can finish loading before hydration attaches onLoad.
  useEffect(() => {
    if (imgRef.current?.complete) setLoaded(true)
  }, [])

  return (
    <Box position="absolute" inset={0} overflow="hidden" aria-hidden="true">
      <Box
        position="absolute"
        inset={0}
        backgroundImage={`url('${BG_PLACEHOLDER}')`}
        backgroundSize="cover"
        backgroundPosition="center"
        filter="blur(24px)"
        transform="scale(1.1)"
      />
      <chakra.img
        ref={imgRef}
        src="/background-1920.webp"
        srcSet="/background-1280.webp 1280w, /background-1920.webp 1920w, /background-2560.webp 2560w"
        sizes="100vw"
        alt=""
        decoding="async"
        fetchPriority="high"
        onLoad={() => setLoaded(true)}
        position="absolute"
        inset={0}
        w="full"
        h="full"
        objectFit="cover"
        opacity={loaded ? 1 : 0}
        transition="opacity 0.4s ease-in"
      />
    </Box>
  )
}

export function AuthLayout({
  title,
  description,
  children,
  maxW = '420px',
}: AuthLayoutProps) {
  const { i18n } = useTranslation()
  const lang = i18n.language.startsWith('uz') ? 'uz' : 'ru'
  const q = QUOTE[lang]

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      px={4}
      py={10}
      bg="bg"
      position="relative"
    >
      <AuthBackground />

      <Box
        {...glassPanel}
        position="relative"
        w="full"
        maxW={maxW}
        borderRadius="2xl"
        boxShadow={{
          base: '0 8px 32px rgba(0, 0, 0, 0.08)',
          _dark: '0 8px 32px rgba(0, 0, 0, 0.45)',
        }}
        px={{ base: 6, sm: 8 }}
        py={8}
      >
        <VStack gap={6} w="full">
          {/* <Box
            w="72px"
            h="40px"
            bg="gray.200"
            borderRadius="md"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Text fontSize="xs" fontWeight="600" color="gray.500" letterSpacing="0.08em">
              LOGO
            </Text>
          </Box> */}

          <Box w="full" textAlign="center">
            <Text
              fontSize={{ base: 'xl', sm: '2xl' }}
              fontWeight="700"
              letterSpacing="-0.02em"
              color="primary.fg"
              lineHeight="1.2"
            >
              {title}
            </Text>
            <Text mt={2} fontSize="sm" color="fg.muted" lineHeight="1.6">
              {description}
            </Text>
          </Box>

          <Box w="full">{children}</Box>

          <Text
            fontSize="xs"
            color="fg.muted"
            textAlign="center"
            lineHeight="1.7"
          >
            {q.helpText}{' '}
            <Text
              as="a"
              color="primary.fg"
              fontWeight="600"
              textDecoration="underline"
              _hover={{ opacity: 0.8 }}
            >
              {q.support}
            </Text>
            .
          </Text>
        </VStack>
      </Box>
    </Flex>
  )
}
